import { useMemo } from 'react'
import { api } from '../api/client'
import type { Exchange, ExchangeQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useExchangeOptions } from '../hooks/useExchangeOptions'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input, Toggle } from '../components/common/Controls'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { MultiSelect } from '../components/common/MultiSelect'
import type { MultiSelectOption } from '../components/common/MultiSelect'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'

const PERMISSION = 'exchange:read'

// Der ganze Bestand auf einer Seite: Es sind knapp neunzig Handelsplätze.
// Durch neunzig Zeilen zu blättern wäre Selbstzweck.
const PAGE_SIZE = 200

/** Sekunden weg — die Handelszeiten stehen auf die Minute genau.
 *
 * Der Endpunkt liefert `time` als "09:30:00". Die Sekunden sind bei einer
 * Handelszeit ohne Aussage und kosten in jeder Zeile Platz.
 */
function uhrzeit(wert: string): string {
  return wert?.slice(0, 5) ?? '—'
}

//: Auswahl und Adresse: kommagetrennt in der Adresse, als Array in der
//: Komponente. Wie auf der Instrumentenseite.
function alsListe(wert: string): string[] {
  return wert ? wert.split(',').filter(Boolean) : []
}

const COLUMNS: Column<Exchange>[] = [
  // Ganz vorn und schmal: Das Kennzeichen wird überflogen, nicht gelesen.
  // Absteigend sortiert stehen die Favoriten oben — deshalb ist die Spalte
  // sortierbar, obwohl sie nur zwei Werte kennt.
  { key: 'favorite', label: 'Fav', sortable: true, align: 'right',
    minWidth: '60px',
    render: (row) => row.favorite
      ? <span title="Favorit" aria-label="Favorit">★</span>
      : <span aria-hidden="true">·</span> },
  { key: 'code', label: 'MIC', sortable: true, mono: true, copy: true,
    minWidth: '90px',
    help: 'Market Identifier Code nach ISO 10383, vergeben von SWIFT. Vier Zeichen, weltweit eindeutig. Über ihn hängen die Instrumente an ihrem Handelsplatz — er ist der Schlüssel, nicht das Kürzel daneben.' },
  { key: 'alias', label: 'Kürzel', sortable: true, mono: true, minWidth: '110px',
    help: 'Das gebräuchliche Kürzel für die Anzeige — NASDAQ statt XNAS. Nicht eindeutig: BSE tragen Bombay, Budapest und Botswana. Zum Verknüpfen dient allein der MIC.',
    render: (row) => row.alias || '—' },
  { key: 'name', label: 'Handelsplatz', sortable: true, minWidth: '260px' },
  { key: 'currency', label: 'Währung', sortable: true, mono: true,
    minWidth: '100px',
    help: 'Die Währung, in der an diesem Platz notiert wird. Kurse desselben Instruments sind zwischen zwei Plätzen deshalb nicht ohne Umrechnung vergleichbar.' },
  // Die Zeiten rechtsbündig und einheitlich breit: So stehen Stunden unter
  // Stunden, und die Spalte lässt sich von oben nach unten überfliegen.
  { key: 'open_time', label: 'Eröffnung', sortable: true, mono: true,
    align: 'right', minWidth: '110px',
    help: 'Beginn des fortlaufenden Handels in ORTSZEIT des Handelsplatzes. Eröffnungsauktionen liegen teils davor.',
    render: (row) => uhrzeit(row.open_time) },
  { key: 'close_time', label: 'Schluss', sortable: true, mono: true,
    align: 'right', minWidth: '100px',
    help: 'Ende des fortlaufenden Handels in Ortszeit. Schlussauktionen liegen teils danach.',
    render: (row) => uhrzeit(row.close_time) },
  { key: 'timezone', label: 'Zeitzone', sortable: true, mono: true,
    minWidth: '200px',
    help: 'Zeitzonenname nach IANA. Ohne ihn sind die beiden Uhrzeiten nicht einzuordnen — 09:30 in New York und 09:30 in Tokio liegen vierzehn Stunden auseinander. Die Sommerzeit steckt im Zonennamen, nicht in den Uhrzeiten.' },
]

export function ExchangesPage() {
  const allowed = useHasPermission(PERMISSION)

  const list = useListParams({
    filters: { q: '', currency: '', alias: '', favorite: '' },
    // Favoriten oben: `true` steht in SQL über `false`, also absteigend.
    // Nur die Vorgabe dieser Ansicht — jede Spaltenüberschrift sortiert
    // weiterhin um, und über die Schnittstelle geht ohnehin jede Sortierung.
    sort: 'favorite',
    descending: true,
  })

  // Die Auswahllisten speisen sich aus dem VOLLSTÄNDIGEN Bestand, nicht aus
  // der gerade angezeigten Seite. Sonst verschwänden Optionen, sobald man
  // filtert — wer EUR gewählt hat, sähe USD nicht mehr und käme nicht zurück.
  const { exchanges: alle } = useExchangeOptions(allowed)

  const waehrungOptionen: MultiSelectOption[] = useMemo(() => {
    const zaehler = new Map<string, number>()
    for (const e of alle) {
      zaehler.set(e.currency, (zaehler.get(e.currency) ?? 0) + 1)
    }
    // Nach Häufigkeit: Wer eine Währung sucht, sucht meist eine der großen.
    // Bei gleicher Anzahl alphabetisch, damit die Reihenfolge feststeht.
    return [...zaehler.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([code, anzahl]) => ({
        value: code, label: code,
        hint: `${anzahl} ${anzahl === 1 ? 'Handelsplatz' : 'Handelsplätze'}`,
      }))
  }, [alle])

  const kuerzelOptionen: MultiSelectOption[] = useMemo(
    () => alle
      .filter((e) => e.alias)
      .map((e) => ({ value: e.alias as string, label: e.alias as string,
                     hint: e.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [alle])

  const params: ExchangeQuery = {
    q: list.filters.q.trim() || undefined,
    currency: alsListe(list.filters.currency),
    alias: alsListe(list.filters.alias),
    // Nur senden, wenn der Schalter an ist: `false` hieße "nur die übrigen".
    favorite: list.filters.favorite === '1' ? true : undefined,
    sort: list.sort as ExchangeQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
    // Bei knapp neunzig Zeilen kostet das Zählen nichts und die Statuszeile
    // kann die Gesamtzahl nennen.
    total: true,
  }

  const { result, loading, error, denied } = useListQuery(
    api.exchanges, params, allowed)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Handelsplätze" />
  }

  return (
    <>
      <FilterBar>
        <FilterGrow>
          <Input label="Suche"
                 placeholder="Name, MIC oder Kürzel — auch ein Anfangsstück"
                 value={list.filters.q} autoFocus
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        <FilterFixed>
          <MultiSelect label="Währung" options={waehrungOptionen}
                       selected={alsListe(list.filters.currency)}
                       onChange={(werte) =>
                         list.setFilter('currency', werte.join(','))}
                       placeholder="alle Währungen"
                       searchPlaceholder="EUR, USD …"
                       help="Die Währung, in der an diesem Platz notiert wird. Die Zahl daneben sagt, wie viele Plätze sie führen." />
        </FilterFixed>
        <FilterFixed>
          <MultiSelect label="Kürzel" options={kuerzelOptionen}
                       selected={alsListe(list.filters.alias)}
                       onChange={(werte) =>
                         list.setFilter('alias', werte.join(','))}
                       placeholder="alle Kürzel"
                       searchPlaceholder="NASDAQ, XETRA …"
                       help="Das gebräuchliche Kürzel. Nicht eindeutig — BSE tragen Bombay, Budapest und Botswana; ein Filter darauf trifft dann alle drei." />
        </FilterFixed>
        {/*
          Ein Schalter und keine Mehrfachauswahl: Der Favorit kennt zwei
          Zustände, und "nur Favoriten ODER nur die übrigen" fragt niemand.
          Aus ist deshalb "beide" und nicht "nur die übrigen".
        */}
        <Toggle on={list.filters.favorite === '1'}
                onChange={(an) => list.setFilter('favorite', an ? '1' : '')}>
          nur Favoriten
        </Toggle>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card>
        <DataTable
          columns={COLUMNS}
          rows={result?.items ?? []}
          rowKey={(row) => row.id}
          loading={loading}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={result ? { page: result.page, size: result.size,
                           total: result.total, hasMore: result.has_more }
                       : undefined}
          onPage={list.setPage}
          empty={<EmptyState title="keine Handelsplätze">
            {list.touched
              ? 'Zu Suche und Filtern passt kein Handelsplatz.'
              : 'Es sind noch keine Handelsplätze eingetragen.'}
          </EmptyState>}
        />
      </Card>
    </>
  )
}
